import React, { useState, useEffect, useRef } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import LoadingSpinner from "../components/LoadingSpinner";
import { useActiveModel } from "../context/ActiveModelContext";
import DemoRunner from "../components/DemoRunner";

// Components
import PredictionTable from "../components/PredictionTable";
import MLModelTraining from "../components/MLModelTraining";
import ModelPrediction from "../components/ModelPrediction";
import ModelRegistry from "../components/ModelRegistry";
import ModelComparison from "../components/ModelComparison";
import TrashView from "../components/TrashView";
import ModelPerformance from "../components/ModelPerformance";
import FeatureImportance from "../components/FeatureImportance";
import DatasetPreview from "../components/DatasetPreview";
import DatasetExploration from "../components/DatasetExploration";
import CorrelationAnalysis from "../components/CorrelationAnalysis";
import DataNormalization from "../components/DataNormalization";
import DataVisualization from "../components/DataVisualization";
import MultiOmicsInsights from "../components/MultiOmicsInsights";
import AnnotationManager from "../components/AnnotationManager";
import FeedbackManager from "../components/FeedbackManager";
import FeedbackSidebar from "../components/FeedbackSidebar";
import PricingPage from "./PricingPage";

// API
import { getDatasets, deleteDataset, renameDataset } from "../api";

// Tabs are grouped in workflow order: look at data -> build a model -> read results.
// Utility tabs live in the last group so they stay out of the main flow.
const TAB_GROUPS = [
  { label: "Data", tabs: ["Preview", "Explore", "Multi-Omics Insights"] },
  { label: "Model", tabs: ["Train Model", "Model Registry", "Compare Models"] },
  { label: "Results", tabs: ["Visualizations", "Feature Importance", "Predictions"] },
  { label: "More", tabs: ["Annotations", "Trash", "Feedback", "Pricing"] },
];

const DEMO_VISIBLE_KEY = "biodawn.dashboard.demoVisible";

const WORKFLOW_STEPS = [
  { key: "upload", label: "Upload", tab: "Preview" },
  { key: "train", label: "Train", tab: "Train Model" },
  { key: "validate", label: "Validate", tab: "Model Registry" },
  { key: "interpret", label: "Interpret", tab: "Feature Importance" },
  { key: "export", label: "Export", tab: "Visualizations" },
];

export default function Dashboard({ onUserDashboardClick, onAdminLoginClick }) {
  const [activeTab, setActiveTab] = useState("Preview");
  const [feedbackSidebarOpen, setFeedbackSidebarOpen] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [demoVisible, setDemoVisible] = useState(() => {
    try {
      return window.localStorage.getItem(DEMO_VISIBLE_KEY) !== "false";
    } catch {
      return true;
    }
  });
  const suppressDemoAutoHide = useRef(false);

  const [datasets, setDatasets] = useState([]);
  const [datasetDetails, setDatasetDetails] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [selectedModel, setSelectedModel] = useState("random_forest");
  const modelTypes = ["random_forest", "logistic_regression", "svm", "linear", "neural_network"]; // extend as needed
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { activeModel, clearActiveModel } = useActiveModel();

  const refreshDatasets = async ({ selectName = null } = {}) => {
    const datasetsResponse = await getDatasets();
    const available = datasetsResponse.available_datasets || [];
    const details = datasetsResponse.available_dataset_details || [];
    setDatasets(available);
    setDatasetDetails(details);

    if (selectName && available.includes(selectName)) {
      setSelectedDataset(selectName);
      const selectedDetail = details.find((item) => item.name === selectName);
      setSelectedDatasetId(selectedDetail?.id ?? null);
      return;
    }

    if (selectedDataset && !available.includes(selectedDataset)) {
      if (available.length > 0) {
        setSelectedDataset(available[0]);
        const fallbackDetail = details.find((item) => item.name === available[0]);
        setSelectedDatasetId(fallbackDetail?.id ?? null);
      } else {
        setSelectedDataset(null);
        setSelectedDatasetId(null);
      }
    } else if (selectedDataset) {
      const currentDetail = details.find((item) => item.name === selectedDataset);
      setSelectedDatasetId(currentDetail?.id ?? null);
    }
  };

  // Fetch datasets on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        const datasetsResponse = await getDatasets();
        const available = datasetsResponse.available_datasets || [];
        const details = datasetsResponse.available_dataset_details || [];
        setDatasets(available);
        setDatasetDetails(details);
        if (available.length > 0) {
          const firstDataset = available[0];
          setSelectedDataset(firstDataset);
          const firstDetail = details.find((item) => item.name === firstDataset);
          setSelectedDatasetId(firstDetail?.id ?? null);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching initial data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Listen for demo events (mirror the demo's dataset/model selection)
  useEffect(() => {
    const onSelectDataset = (e) => {
      const ds = e?.detail?.dataset;
      if (ds) setSelectedDataset(ds);
    };
    const onSelectModel = (e) => {
      const mt = e?.detail?.modelType;
      if (mt) setSelectedModel(mt);
    };
    window.addEventListener('demo:selectDataset', onSelectDataset);
    window.addEventListener('demo:selectModel', onSelectModel);
    return () => {
      window.removeEventListener('demo:selectDataset', onSelectDataset);
      window.removeEventListener('demo:selectModel', onSelectModel);
    };
  }, []);

  // Collapse the demo panel once a run has finished, so the dashboard is not
  // permanently topped by the demo controls.
  // Reopening the panel by hand suppresses the auto-hide until the next run starts,
  // otherwise the already-complete demo would immediately hide itself again.
  useEffect(() => {
    const onDemoFinished = () => {
      if (!suppressDemoAutoHide.current) updateDemoVisible(false);
    };
    const onDemoStarted = () => {
      suppressDemoAutoHide.current = false;
    };
    window.addEventListener('demo:finished', onDemoFinished);
    window.addEventListener('demo:started', onDemoStarted);
    return () => {
      window.removeEventListener('demo:finished', onDemoFinished);
      window.removeEventListener('demo:started', onDemoStarted);
    };
  }, []);

  const updateDemoVisible = (visible) => {
    if (visible) suppressDemoAutoHide.current = true;
    setDemoVisible(visible);
    try {
      window.localStorage.setItem(DEMO_VISIBLE_KEY, String(visible));
    } catch {
    }
  };

  const handleDataReceived = async (uploadResponse) => {
    const datasetName = uploadResponse?.dataset_name;
    try {
      await refreshDatasets({ selectName: datasetName || undefined });
    } catch (err) {
      console.error("Error refreshing datasets after upload:", err);
      setError(err.message || "Upload succeeded but dataset list could not be refreshed.");
    }
  };

  const handleDeleteDataset = async (datasetName) => {
    setError(null);
    try {
      await deleteDataset(datasetName);
      if (activeModel?.dataset_name === datasetName) {
        clearActiveModel();
      }
      await refreshDatasets();
    } catch (err) {
      setError(err.message || `Failed to delete dataset "${datasetName}".`);
      throw err;
    }
  };

  const handleRenameDataset = async (oldName, newName) => {
    setError(null);
    try {
      await renameDataset(oldName, newName);
      if (activeModel?.dataset_name === oldName) {
        clearActiveModel();
      }
      await refreshDatasets({ selectName: newName });
    } catch (err) {
      setError(err.message || `Failed to rename dataset "${oldName}".`);
      throw err;
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case "Preview":
        return (
          <>
            <DatasetPreview datasetName={selectedDataset} />
            {/* Show available datasets if loaded */}
            {datasets.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <h3>Available Datasets</h3>
                <ul>
                  {datasets.map((dataset, index) => (
                    <li key={index}>{dataset}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        );
      case "Explore":
        return (
          <>
            <DatasetExploration datasetName={selectedDataset} />
            <CorrelationAnalysis datasetName={selectedDataset} />
            {/* <DataNormalization datasetName={selectedDataset} /> */}
          </>
        );
      case "Train Model":
        return (
          <MLModelTraining
            datasets={datasets}
            datasetName={selectedDataset}
            datasetId={selectedDatasetId}
            modelType={selectedModel}
            onModelTypeChange={setSelectedModel}
          />
        );
      case "Predictions":
        return (
          <>
            <ModelPrediction 
              datasets={datasets}
              selectedDataset={selectedDataset}
              modelTypes={modelTypes}
              selectedModel={selectedModel}
            />
            <PredictionTable datasetName={selectedDataset} modelType={selectedModel} />
          </>
        );
      case "Model Registry":
        return <ModelRegistry />;
      case "Compare Models":
        return <ModelComparison />;
      case "Feature Importance":
        return <FeatureImportance modelType={activeModel?.model_type || selectedModel} datasetName={activeModel?.dataset_name || selectedDataset} />;
      case "Visualizations":
        return (
          <>
            <DataVisualization 
              datasetName={activeModel?.dataset_name || selectedDataset || "test"} 
              modelType={activeModel?.model_type || selectedModel}
              modelId={activeModel?.id || null}
            />
            <ModelPerformance modelType={activeModel?.model_type || selectedModel} />
          </>
        );
      case "Multi-Omics Insights":
        return (
          <MultiOmicsInsights 
            datasets={datasets}
            defaultDataset={selectedDataset}
          />
        );
      case "Trash":
        return <TrashView />;
      case "Annotations":
        return <AnnotationManager />;
      case "Feedback":
        return <FeedbackManager />;
      case "Pricing":
        return <PricingPage />;
      default:
        return null;
    }
  };

  const workflowStatus = {
    upload: datasets.length > 0,
    train: Boolean(activeModel?.id),
    validate: Boolean(activeModel?.id),
    interpret: Boolean(activeModel?.id),
    export: Boolean(activeModel?.id),
  };

  if (loading) {
    return (
      <DashboardLayout
        datasets={datasets}
        selectedDataset={selectedDataset}
        onSelectDataset={setSelectedDataset}
        onSelectDatasetId={setSelectedDatasetId}
        datasetDetails={datasetDetails}
        modelTypes={modelTypes}
        selectedModel={selectedModel}
        onSelectModel={setSelectedModel}
        onDataReceived={handleDataReceived}
        onDeleteDataset={handleDeleteDataset}
        onRenameDataset={handleRenameDataset}
        onFeedbackClick={() => setFeedbackSidebarOpen(true)}
      >
        <div style={{
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <LoadingSpinner size="large" text="Loading dashboard data..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <DashboardLayout
        datasets={datasets}
        selectedDataset={selectedDataset}
        onSelectDataset={setSelectedDataset}
        onSelectDatasetId={setSelectedDatasetId}
        datasetDetails={datasetDetails}
        modelTypes={modelTypes}
        selectedModel={selectedModel}
        onSelectModel={setSelectedModel}
        onDataReceived={handleDataReceived}
        onDeleteDataset={handleDeleteDataset}
        onRenameDataset={handleRenameDataset}
        onFeedbackClick={() => setFeedbackSidebarOpen(true)}
        onUserDashboardClick={onUserDashboardClick}
        onAdminLoginClick={onAdminLoginClick}
      >
        {/* Error message */}
        {error && (
          <div
            style={{
              color: "red",
              padding: "10px",
              margin: "10px",
              backgroundColor: "#ffe6e6",
              border: "1px solid #ff9999",
            }}
          >
            Error: {error}
          </div>
        )}

        {/* Demo Runner - hidden once a demo run finishes, re-openable from the toggle */}
        <div className="demo-toggle-row">
          <button
            type="button"
            className="btn btn--outline demo-toggle"
            onClick={() => updateDemoVisible(!demoVisible)}
            aria-expanded={demoVisible}
            aria-controls="demo-runner-panel"
          >
            {demoVisible ? "Hide demo" : "Show demo"}
          </button>
        </div>

        {demoVisible && (
          <div id="demo-runner-panel">
            <DemoRunner />
          </div>
        )}

        {/* Thin side pull-tab to open guidance panel (kept off the top-right to avoid the account button) */}
        <button
          type="button"
          onClick={() => setGuidanceOpen((prev) => !prev)}
          title={guidanceOpen ? "Close workflow guidance" : "Open workflow guidance"}
          aria-expanded={guidanceOpen}
          aria-controls="workflow-guidance-panel"
          style={{
            position: "fixed",
            right: guidanceOpen ? 312 : 0,
            top: "50%",
            transform: "translateY(-50%) rotate(180deg)",
            transformOrigin: "center center",
            zIndex: 21,
            width: 28,
            minHeight: 120,
            padding: "12px 4px",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            border: "1px solid var(--border)",
            borderRight: "none",
            background: "var(--primary, #2563eb)",
            color: "#fff",
            cursor: "pointer",
            boxShadow: "-2px 0 8px rgba(0,0,0,0.12)",
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 1,
            transition: "right 0.2s ease",
          }}
        >
          {guidanceOpen ? "Close ›" : "‹ Guidance"}
        </button>

        {guidanceOpen && (
          <aside
            id="workflow-guidance-panel"
            role="complementary"
            aria-label="Workflow guidance"
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              width: 300,
              height: "100vh",
              overflowY: "auto",
              zIndex: 19,
              padding: "16px 14px",
              borderLeft: "1px solid var(--border)",
              background: "var(--bg-light, #fafafa)",
              boxShadow: "-6px 0 24px rgba(0,0,0,0.12)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontWeight: 600 }}>Workflow Guidance</div>
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => setGuidanceOpen(false)}
                style={{ padding: "4px 10px", fontSize: 12 }}
                aria-label="Close guidance panel"
              >
                Close
              </button>
            </div>
            <details style={{ marginBottom: 10 }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>Quick start (1 minute)</summary>
              <ol style={{ margin: "8px 0 0 18px", fontSize: 13, color: "var(--text-muted)" }}>
                <li>Upload a dataset in the left sidebar.</li>
                <li>Train/select a model in Train Model.</li>
                <li>Validate model health and thresholds in Model Registry.</li>
                <li>Run pathway/interpretation, then export high-confidence outputs.</li>
              </ol>
            </details>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 8 }}>
              {WORKFLOW_STEPS.map((step, index) => (
                <button
                  key={step.key}
                  onClick={() => {
                    setActiveTab(step.tab);
                    setGuidanceOpen(false);
                  }}
                  className={`btn ${activeTab === step.tab ? "btn--primary" : "btn--outline"}`}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}
                  title={`Go to ${step.label}`}
                >
                  <span>{index + 1}. {step.label}</span>
                  <span style={{ fontSize: 12 }}>{workflowStatus[step.key] ? "✓" : "○"}</span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Keep this order for best reliability: upload -> train -> validate -> interpret -> export.
            </div>
          </aside>
        )}

        <nav className="tabs">
          {TAB_GROUPS.map((group) => (
            <React.Fragment key={group.label}>
              <span className="tabs__group-label">{group.label}</span>
              {group.tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`tab ${activeTab === tab ? "active" : ""} ${tab.toLowerCase().replace(/ /g, '-')}-tab`}
                >
                  {tab}
                </button>
              ))}
            </React.Fragment>
          ))}
        </nav>

        {/* Active Tab Content */}
        {renderTab()}
      </DashboardLayout>

      {/* Feedback Sidebar */}
      <FeedbackSidebar
        isOpen={feedbackSidebarOpen}
        onClose={() => setFeedbackSidebarOpen(false)}
      />


    </>
  );
}
